import { useEffect, useState, type FormEvent } from 'react';
import QRCode from 'qrcode';
import {
  beginMfaSetup,
  confirmMfaSetup,
  disableMfa,
  getMfaStatus,
  translateCognitoError,
  type MfaEnrollment,
} from '../auth/auth';
import Spinner from './Spinner';

/** マイページに埋め込む二段階認証（TOTP / 認証アプリ）の設定セクション。 */
export default function MfaSettings() {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [error, setError] = useState('');

  // 登録フロー（有効化ボタン → QRコード表示 → コード確認）の状態。
  const [enrollment, setEnrollment] = useState<MfaEnrollment | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getMfaStatus()
      .then((status) => {
        if (!cancelled) setEnabled(status.enabled);
      })
      .catch((e) => {
        if (!cancelled) setError(translateCognitoError(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const startEnrollment = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await beginMfaSetup();
      const dataUrl = await QRCode.toDataURL(result.otpauthUri);
      setEnrollment(result);
      setQrDataUrl(dataUrl);
    } catch (e) {
      setError(translateCognitoError(e));
    } finally {
      setBusy(false);
    }
  };

  const cancelEnrollment = () => {
    setEnrollment(null);
    setQrDataUrl('');
    setCode('');
    setError('');
  };

  const onConfirm = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await confirmMfaSetup(code);
      setEnabled(true);
      cancelEnrollment();
      window.alert('二段階認証を有効にしました');
    } catch (e) {
      setError(translateCognitoError(e));
    } finally {
      setBusy(false);
    }
  };

  const onDisable = async () => {
    if (!window.confirm('二段階認証を無効にしますか？')) return;
    setBusy(true);
    setError('');
    try {
      await disableMfa();
      setEnabled(false);
    } catch (e) {
      setError(translateCognitoError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div style={{ maxWidth: 420 }}>
        <h3 style={{ margin: 0 }}>二段階認証</h3>
        <Spinner />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 420 }}>
      <h3 style={{ margin: 0 }}>二段階認証</h3>
      <p style={{ margin: 0 }}>
        認証アプリ（Google Authenticator 等）を使った二段階認証（TOTP）を設定できます。
        管理者アカウントは、運用によりログイン時に必須化される場合があります。
      </p>

      {error && <div className="auth-form__error">{error}</div>}

      {enabled && !enrollment && (
        <>
          <p className="notice" style={{ margin: 0 }}>
            二段階認証は有効です。
          </p>
          <div>
            <button className="btn" type="button" disabled={busy} onClick={onDisable}>
              {busy ? '処理中…' : '無効化する'}
            </button>
          </div>
        </>
      )}

      {!enabled && !enrollment && (
        <div>
          <button className="btn btn--primary" type="button" disabled={busy} onClick={startEnrollment}>
            {busy ? '準備中…' : '有効化する'}
          </button>
        </div>
      )}

      {enrollment && (
        <form className="auth-form" onSubmit={onConfirm}>
          <p style={{ margin: 0 }}>
            認証アプリで以下のQRコードを読み取るか、シークレットキーを手動で入力してください。
          </p>
          {qrDataUrl && <img src={qrDataUrl} alt="MFA 設定用 QR コード" width={200} height={200} />}
          <div className="field">
            <label htmlFor="mfaSecret">シークレットキー（手動入力用）</label>
            <input id="mfaSecret" type="text" readOnly value={enrollment.secretCode} />
          </div>
          <div className="field">
            <label htmlFor="mfaConfirmCode">認証アプリに表示された6桁コード</label>
            <input
              id="mfaConfirmCode"
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              required
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn--primary" type="submit" disabled={busy}>
              {busy ? '確認中…' : '確認して有効化'}
            </button>
            <button className="btn" type="button" disabled={busy} onClick={cancelEnrollment}>
              キャンセル
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
